import { Button, Divider, Flex, Form, Space, Table } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { normalizeClientData, normalizeClientDataForExport } from './normalize';
import { ClientForm } from './clientsForm';
import { ActiveColumn } from '../../common/tableCustomColumns';
import { ButtonExportData } from '../../common';
import { GET_CUSTOMERS_PAGINATED } from './queryClientsGQL';
import { RemoteSearchClients } from '../../common/SearchClients';

const PAGE_SIZE = 50;

export const ClientsManagement = () => {
  const [clientList, setClientList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [form] = Form.useForm();
  const [openClientForm, setOpenClientForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [filteredClient, setFilteredClient] = useState(null);
  const [exportData, setExportData] = useState([]);
  const wasEditedRef = useRef(false);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, loading, refetch } = useQuery(GET_CUSTOMERS_PAGINATED, {
    variables: { limit: PAGE_SIZE, offset },
    fetchPolicy: 'network-only'
  });

  // --- Función central para actualizar la tabla ---
  const updateTable = (customers) => {
    const { dataSource, columns } = normalizeClientData(customers);
    const exportDataNormalized = normalizeClientDataForExport(customers);

    setClientList(dataSource);
    setColumns(columns);
    setExportData(exportDataNormalized);
  };

  // useEffect inicial o cuando cambia la data global
  useEffect(() => {
    if (data?.customersPaginated) {
      updateTable(data.customersPaginated.customers);
    }
  }, [data]);

  const editClient = (cliData) => {
    setOpenClientForm(true);
    setSelectedClient(cliData);
  };

  const columnsTable = useMemo(() => [...(columns ?? []), ActiveColumn], [columns]);

  const handleClientSelect = (client) => {
    setFilteredClient(client);

    if (!client) {
      form.setFieldsValue({ filterClients: null });
      setOffset(0);
      setCurrentPage(1);
      setFilteredClient(null);

      // refetch y actualizar tabla directamente
      refetch({ limit: PAGE_SIZE, offset: 0, searchString: null }).then((result) => {
        const customers = result.data?.customersPaginated?.customers ?? [];
        updateTable(customers);
      });

      return;
    }

    // Si seleccionas un cliente, solo mostrar ese
    updateTable([client]);
    setCurrentPage(1);
  };

  const handleModalClose = () => {
    setOpenClientForm(false);
    setSelectedClient(null);

    if (wasEditedRef.current) {
      if (filteredClient) {
        // recargar solo ese cliente
        refetch({ limit: 1, offset: 0, searchString: filteredClient.clie_rfc }).then((result) => {
          const customers = result.data?.customersPaginated?.customers ?? [];
          updateTable(customers);
        });
      } else {
        // recargar toda la tabla
        refetch({ limit: PAGE_SIZE, offset }).then((result) => {
          const customers = result.data?.customersPaginated?.customers ?? [];
          updateTable(customers);
        });        
      }
    }
    wasEditedRef.current = false;
  };

  return (
    <div className="basic-layout">
      <Flex justify="space-between" align="center" className="basic-layout-header">
        <h1>Listado de Clientes</h1>
        <Space>
          <Form form={form}>
            <Form.Item
              name="filterClients"
              label="Buscar Cliente"
              labelAlign="left"
              layout="vertical"
              style={{ width: '500px' }}>
              <RemoteSearchClients onSelectClient={handleClientSelect} />
            </Form.Item>
          </Form>

          <Button
            type="primary"
            onClick={() => {
              setOpenClientForm(true);
              setSelectedClient(null);
            }}>
            Agregar Cliente
          </Button>
        </Space>
      </Flex>

      <Divider />

      <Flex justify="end" style={{ marginBottom: '1rem' }}>
        <ButtonExportData jsonData={exportData} fileName="Clientes" />
      </Flex>

      <Table
        dataSource={clientList}
        size="small"
        columns={columnsTable}
        style={{ cursor: 'pointer' }}
        loading={loading}
        scrollToFirstRowOnChange
        scroll={{ y: window.innerHeight - 450 }}
        onRow={(record) => ({ onClick: () => editClient(record) })}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: data?.customersPaginated?.totalRecords,
          showSizeChanger: false,
          onChange: (page) => {
            const newOffset = (page - 1) * PAGE_SIZE;
            setCurrentPage(page);
            setOffset(newOffset);
            refetch({ limit: PAGE_SIZE, offset: newOffset }).then(({ data }) => {
              if (data?.customersPaginated) updateTable(data.customersPaginated.customers);
            });
          }
        }}
      />

      <ClientForm
        open={openClientForm}
        selectedClient={selectedClient}
        setWasEdited={wasEditedRef}
        onClose={handleModalClose}
      />
    </div>
  );
};
